"use client";

import Link from "next/link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TrendingUp, TrendingDown, Eye, Heart, MessageCircle, Share2, ArrowRight } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { cn } from "@/lib/utils";
import { mockPosts, platformIcons, statusConfig, mockClients } from "@/lib/mock-data";
import { useClient } from "@/lib/client-context";

const platformStats = [
  { platform: "Instagram", key: "INSTAGRAM", followers: "24.8K", engagement: "4.2%", color: "bg-pink-500" },
  { platform: "LinkedIn", key: "LINKEDIN", followers: "12.5K", engagement: "3.8%", color: "bg-blue-700" },
  { platform: "Twitter/X", key: "TWITTER", followers: "18.2K", engagement: "2.1%", color: "bg-slate-800" },
  { platform: "Facebook", key: "FACEBOOK", followers: "8.3K", engagement: "1.9%", color: "bg-blue-600" },
];

const weeklyData = [
  { day: "Mon", posts: 3, engagement: 45 },
  { day: "Tue", posts: 5, engagement: 72 },
  { day: "Wed", posts: 2, engagement: 38 },
  { day: "Thu", posts: 4, engagement: 65 },
  { day: "Fri", posts: 6, engagement: 89 },
  { day: "Sat", posts: 1, engagement: 22 },
  { day: "Sun", posts: 2, engagement: 31 },
];

export default function AnalyticsPage() {
  const { selectedClientId } = useClient();

  const clientPosts = mockPosts.filter(
    (p) => selectedClientId === "all" || p.clientId === selectedClientId
  );

  const postsWithEngagement = clientPosts
    .filter((p) => p.engagement)
    .sort((a, b) => (b.engagement?.likes ?? 0) - (a.engagement?.likes ?? 0));

  const totalReach = clientPosts.reduce((sum, p) => sum + (p.engagement?.reach ?? 0), 0);
  const totalLikes = clientPosts.reduce((sum, p) => sum + (p.engagement?.likes ?? 0), 0);
  const totalComments = clientPosts.reduce((sum, p) => sum + (p.engagement?.comments ?? 0), 0);
  const totalShares = clientPosts.reduce((sum, p) => sum + (p.engagement?.shares ?? 0), 0);

  const selectedClient = selectedClientId !== "all"
    ? mockClients.find((c) => c.id === selectedClientId)
    : null;

  const stats = [
    { label: "Total Reach", value: totalReach > 1000 ? `${(totalReach / 1000).toFixed(1)}K` : String(totalReach), change: "+12.3%", trending: "up" as const, icon: Eye },
    { label: "Engagement", value: totalLikes.toLocaleString(), change: "+8.7%", trending: "up" as const, icon: Heart },
    { label: "Comments", value: totalComments.toLocaleString(), change: "+23.1%", trending: "up" as const, icon: MessageCircle },
    { label: "Shares", value: totalShares.toLocaleString(), change: "-2.4%", trending: "down" as const, icon: Share2 },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-8 py-5 border-b bg-card">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {selectedClient ? `${selectedClient.name} Analytics` : "Analytics"}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Track your content performance across all platforms
          </p>
        </div>
        <Select defaultValue="30d">
          <SelectTrigger className="w-[160px] h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            <SelectItem value="year">This year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex-1 p-8 overflow-auto space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <stat.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className={cn(
                    "flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full",
                    stat.trending === "up" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                  )}>
                    {stat.trending === "up" ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {stat.change}
                  </div>
                </div>
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Weekly Activity Chart */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Weekly Activity</CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={weeklyData} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--chart-grid)" />
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "var(--chart-text)" }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "var(--chart-text)" }} width={30} />
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid var(--chart-grid)", fontSize: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", background: "#fff" }}
                    cursor={{ fill: "var(--chart-primary-light)", radius: 4 }}
                  />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }}
                  />
                  <Bar dataKey="posts" name="Posts" fill="var(--chart-primary)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="engagement" name="Engagement" fill="var(--chart-primary-light)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Platform Breakdown */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">By Platform</CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              {(() => {
                const pieData = platformStats.map((p) => ({
                  name: p.platform,
                  value: clientPosts.filter((post) => post.platform === p.key).length,
                }));
                const COLORS = ["#ec4899", "#1d4ed8", "#334155", "#2563eb"];
                return (
                  <>
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          paddingAngle={3}
                          dataKey="value"
                          stroke="none"
                        >
                          {pieData.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ borderRadius: "8px", border: "1px solid var(--chart-grid)", fontSize: "12px", background: "#fff" }}
                          formatter={(value) => [`${value} posts`, ""]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2 mt-2">
                      {platformStats.map((p, i) => {
                        const postCount = clientPosts.filter((post) => post.platform === p.key).length;
                        return (
                          <div key={p.platform} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                              <span className="text-muted-foreground">{p.platform}</span>
                            </div>
                            <span className="font-medium">{postCount} posts</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Top Performing Posts */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Top Performing Posts</CardTitle>
              <Link href="/content/posts" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground transition-colors">
                View all <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-1">
              {(postsWithEngagement.length > 0
                ? postsWithEngagement
                : clientPosts.slice(0, 4)
              ).map((post, i) => {
                const platform = platformIcons[post.platform];
                return (
                  <Link
                    key={post.id}
                    href={`/analytics/${post.id}`}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <span className="text-lg font-bold text-muted-foreground/50 w-6 text-center">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{post.content}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded", platform?.bg, platform?.color)}>
                          {platform?.label}
                        </span>
                        <span className="text-[11px] text-muted-foreground">by {post.author}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold">
                        {post.engagement ? post.engagement.likes.toLocaleString() : "—"} likes
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {post.engagement ? `${(post.engagement.reach / 1000).toFixed(1)}K reach` : "No data yet"}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* All Posts Performance Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">All Posts Performance</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="rounded-lg border overflow-hidden">
              <div className="grid grid-cols-[1fr_100px_80px_80px_80px_80px] gap-4 px-4 py-2.5 border-b bg-muted/30 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span>Post</span>
                <span>Platform</span>
                <span className="text-right">Likes</span>
                <span className="text-right">Comments</span>
                <span className="text-right">Shares</span>
                <span className="text-right">Reach</span>
              </div>
              {clientPosts.map((post) => {
                const platform = platformIcons[post.platform];
                const status = statusConfig[post.status];
                return (
                  <Link
                    key={post.id}
                    href={`/analytics/${post.id}`}
                    className="grid grid-cols-[1fr_100px_80px_80px_80px_80px] gap-4 px-4 py-3 border-b last:border-b-0 hover:bg-muted/30 transition-colors items-center"
                  >
                    <div className="min-w-0 flex items-center gap-2">
                      <span className={cn("h-2 w-2 rounded-full shrink-0", status.dot)} />
                      <p className="text-sm truncate">{post.content}</p>
                    </div>
                    <span className={cn("text-[11px] font-medium px-1.5 py-0.5 rounded w-fit", platform?.bg, platform?.color)}>
                      {platform?.label}
                    </span>
                    <span className="text-sm text-right font-medium">
                      {post.engagement?.likes.toLocaleString() ?? "—"}
                    </span>
                    <span className="text-sm text-right font-medium">
                      {post.engagement?.comments ?? "—"}
                    </span>
                    <span className="text-sm text-right font-medium">
                      {post.engagement?.shares ?? "—"}
                    </span>
                    <span className="text-sm text-right font-medium">
                      {post.engagement ? `${(post.engagement.reach / 1000).toFixed(1)}K` : "—"}
                    </span>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
